'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { Cable, ChartNoAxesCombined, ClipboardCheck, Receipt } from 'lucide-react';

import { cn } from '@/lib/utils';
import { SectionShell } from '@/components/custom/section-shell';
import { CtaLink } from '@/components/custom/cta-link';

const steps = [
  {
    id: 'crear',
    title: 'Creá reparaciones',
    description: 'Cargá el equipo, el cliente y la falla en segundos, con su código único.',
    image: '/screens/1.png',
    icon: Cable,
  },
  {
    id: 'diagnosticar',
    title: 'Realizá diagnósticos',
    description: 'Documentá pruebas y hallazgos, y definí si el equipo es reparable.',
    image: '/screens/2.png',
    icon: ChartNoAxesCombined,
  },
  {
    id: 'presupuestar',
    title: 'Generá presupuestos',
    description: 'Armá el presupuesto con repuestos y mano de obra, y enviálo por email.',
    image: '/screens/3.png',
    icon: Receipt,
  },
  {
    id: 'notificar',
    title: 'Notificá a tus clientes',
    description: 'El cliente sigue el estado con su link. Menos llamados, más confianza.',
    image: '/screens/4.png',
    icon: ClipboardCheck,
  },
];

const STEP_MS = 6500;

const HowItWorks = () => {
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setProgress(0);
    const tick = setInterval(() => setProgress((p) => Math.min(100, p + 100 / (STEP_MS / 50))), 50);
    const advance = setTimeout(() => setActive((s) => (s + 1) % steps.length), STEP_MS);

    return () => {
      clearInterval(tick);
      clearTimeout(advance);
    };
  }, [active]);

  // Mobile timeline: fill the vertical rail and light up markers based on scroll
  // position (mirrors the desktop rail, but scroll-driven). A trigger line ~68%
  // down the viewport is the "playhead"; the rail fills to it and each marker it
  // passes turns solid.
  const mobileListRef = useRef<HTMLOListElement>(null);
  const mobileFillRef = useRef<HTMLSpanElement>(null);
  const markerRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const [mobileActive, setMobileActive] = useState(0);

  useEffect(() => {
    if (!window.matchMedia('(max-width: 1023px)').matches) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setMobileActive(steps.length);

      if (mobileFillRef.current) {
        mobileFillRef.current.style.transform = 'translateX(-50%) scaleY(1)';
      }

      return;
    }

    let raf = 0;

    const update = () => {
      raf = 0;
      const ol = mobileListRef.current;
      const markers = markerRefs.current.filter(Boolean) as HTMLSpanElement[];

      if (!ol || markers.length < 2) return;

      const centerY = (el: HTMLElement) => el.getBoundingClientRect().top + el.offsetHeight / 2;
      // Playhead a bit past mid-viewport so the fill starts a touch earlier.
      const triggerY = window.innerHeight * 0.68;
      const rect = ol.getBoundingClientRect();
      // Rail runs the full line: first marker center (top-5) → the line's end
      // below the last marker (bottom-5), matching the base rail element.
      const railTop = rect.top + 20;
      const railBottom = rect.bottom - 20;
      const len = railBottom - railTop || 1;
      const pct = Math.max(0, Math.min(1, (triggerY - railTop) / len));

      if (mobileFillRef.current) {
        // scaleY (GPU-composited) instead of height — no per-frame layout, so
        // the fill tracks the scroll smoothly (per next-sanity-animations skill).
        mobileFillRef.current.style.transform = `translateX(-50%) scaleY(${pct})`;
      }

      let count = 0;

      for (const m of markers) if (centerY(m) <= triggerY + 1) count++;

      setMobileActive((prev) => (prev !== count ? count : prev));
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);

      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <SectionShell
      id="funciones"
      eyebrow="Cómo funciona"
      title="De la orden al retiro, en un solo flujo"
      subtitle="Centralizá clientes, presupuestos, órdenes y seguimiento sin planillas ni procesos dispersos."
    >
      <div className="mt-14">
        {/* Mobile / tablet — a connected numbered timeline. This section is a
            SEQUENCE (orden → retiro), so on small screens (where the desktop
            screenshots are hidden) we render it as a flow, not another card
            grid like the Módulos section above it. */}
        <ol ref={mobileListRef} className="relative flex flex-col gap-6 lg:hidden">
          {/* Base rail + scroll-driven fill, spanning first→last marker center */}
          <span
            aria-hidden
            className="absolute top-5 bottom-5 left-5 w-px -translate-x-1/2 bg-line"
          />
          <span
            ref={mobileFillRef}
            aria-hidden
            className="absolute top-5 bottom-5 left-5 w-px origin-top bg-brand transition-transform duration-100 ease-out will-change-transform"
            style={{ transform: 'translateX(-50%) scaleY(0)' }}
          />
          {steps.map((step, index) => {
            const isActive = index < mobileActive;

            return (
              <li key={step.id} className="relative flex gap-4">
                <span
                  ref={(el) => {
                    markerRefs.current[index] = el;
                  }}
                  className={cn(
                    'relative z-10 flex size-10 shrink-0 items-center justify-center rounded-full border transition-colors duration-300',
                    isActive
                      ? 'border-transparent bg-brand text-brand-ink'
                      : 'border-brand/20 bg-brand/8 text-brand',
                  )}
                >
                  <step.icon className="size-5" />
                </span>
                <div className="pb-1">
                  <span className="font-mono text-xs font-medium tracking-wider text-brand dark:text-brand-lift">
                    0{index + 1}
                  </span>
                  <h3 className="font-display font-semibold text-ink">{step.title}</h3>
                  <p className="mt-1 text-sm text-ink-soft">{step.description}</p>
                </div>
              </li>
            );
          })}
        </ol>

        {/* Desktop — the same sequence, horizontal: numbered markers on a rail
            that fills as the flow auto-advances. Markers drive the screenshot. */}
        <ol className="relative hidden grid-cols-4 gap-4 lg:grid">
          {/* Rail spans between the first and last marker centers (each at the
              12.5% column center). */}
          <span
            aria-hidden
            className="absolute top-6 right-[12.5%] left-[12.5%] h-0.5 -translate-y-1/2 rounded-full bg-line"
          />
          <span
            aria-hidden
            className="absolute top-6 left-[12.5%] h-0.5 -translate-y-1/2 rounded-full bg-brand transition-[width] duration-100 ease-linear"
            style={{ width: `${((active + progress / 100) / (steps.length - 1)) * 75}%` }}
          />
          {steps.map((step, index) => {
            const isActive = index === active;

            return (
              <li key={step.id}>
                <button
                  type="button"
                  onClick={() => setActive(index)}
                  aria-current={isActive}
                  className="group flex w-full flex-col items-center gap-3 text-center"
                >
                  <span
                    className={cn(
                      'relative z-10 flex size-12 items-center justify-center rounded-full border bg-paper transition-colors',
                      isActive
                        ? 'border-transparent bg-brand text-brand-ink'
                        : 'border-brand/25 text-brand group-hover:border-brand/50',
                    )}
                  >
                    <step.icon className="size-5" />
                  </span>
                  <span className="font-mono text-xs font-medium tracking-wider text-brand dark:text-brand-lift">
                    0{index + 1}
                  </span>
                  <span className="font-display font-semibold text-ink">{step.title}</span>
                  <span className="max-w-[26ch] text-sm text-ink-soft">{step.description}</span>
                </button>
              </li>
            );
          })}
        </ol>

        {/* Product — desktop only (the auto-advancing image is meaningless on mobile) */}
        <div className="mt-8 hidden overflow-hidden rounded-xl border border-line bg-surface shadow-[var(--shadow-md)] lg:block">
          <div className="flex items-center gap-2 border-b border-line px-4 py-3">
            <span className="size-2.5 rounded-full bg-[#ff5f57]" />
            <span className="size-2.5 rounded-full bg-[#febc2e]" />
            <span className="size-2.5 rounded-full bg-[#28c840]" />
          </div>
          <div className="relative aspect-[16/10] w-full sm:aspect-[1738/820]">
            {steps.map((step, index) => (
              <Image
                key={step.id}
                src={step.image}
                alt={step.title}
                fill
                sizes="(min-width: 1024px) 1120px, 100vw"
                className={cn(
                  'object-cover object-top transition-opacity duration-500',
                  index === active ? 'opacity-100' : 'opacity-0',
                )}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="mt-14 flex flex-col items-center gap-4 text-center">
        <p className="text-lead text-ink-soft">
          ¿Listo para trabajar más rápido y ordenado con Mendly?
        </p>
        <CtaLink href="#precio" event={{ cta: 'howitworks_empezar' }}>
          Empezar hoy
        </CtaLink>
      </div>
    </SectionShell>
  );
};

export default HowItWorks;
